import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BsModalService } from 'ngx-bootstrap/modal';

import { Offer } from '../../../model/offer';
import { SharedService, UiOfferTab } from '../../../_services/shared.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { ThemeService } from '../../../_services/theme.service';
import { OfferCategoryService } from '../../../_services/offer-category.service';
import { TooltipService } from '../../../_services/tooltip.service';
import { UiNewOfferPhotoGalleryComponent } from '../ui-new-offer-photo-gallery/ui-new-offer-photo-gallery.component';

@Component({
  selector: 'ui-new-offer-card-ancillary',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-new-offer-card-ancillary.component.html',
  styleUrl: './ui-new-offer-card-ancillary.component.scss',
})
export class UiNewOfferCardAncillaryComponent implements OnInit {
  private bo = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  public tooltipService = inject(TooltipService);

  @Input() offers!: Offer[] | null;
  @Input() offer!: Offer | null;
  @Input() index = 0;
  @Input() viewMoreToggle = false;
  @Input() propertyId = '';
  @Input() offerAliasId = '';
  @Input() viewMode: 'tiles' | 'list' = 'tiles';

  isHandset = false;
  infoTooltipOpen = false;

  constructor(
    public sharedService: SharedService,
    public themeService: ThemeService,
    public shoppingCartService: ShoppingCartService,
    private offerCategoryService: OfferCategoryService,
    private modalService: BsModalService,
  ) {
    const sub = this.bo.observe([Breakpoints.HandsetPortrait]).subscribe(state => {
      this.isHandset = state.matches;
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  ngOnInit(): void {
    const activeTab = this.sharedService.getOfferCurrentActiveFilter() as UiOfferTab;
    this.sharedService.markUpgradeTabSeen(activeTab);
  }

  isTilesMode(): boolean {
    return this.viewMode === 'tiles';
  }

  shouldUseVerticalLayout(): boolean {
    return this.isTilesMode() || this.isHandset;
  }

  hasImage(): boolean {
    const urls = this.offer?.amenities?.room_photo_urls || [];
    return urls.length > 0 || !!this.offer?.amenities?.room_photo_url;
  }

  getImageUrl(): string {
    const urls = this.offer?.amenities?.room_photo_urls || [];
    return urls[0] || this.offer?.amenities?.room_photo_url || '';
  }

  openPhotoGallery(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.offer) {
      return;
    }

    this.sharedService.setSelectedOfferByOffer(this.index, this.offer, false);

    this.modalService.show(UiNewOfferPhotoGalleryComponent, {
      ...this.sharedService.config,
      initialState: {
        offer: this.sharedService.offer,
        index: this.index,
        propertyId: this.propertyId,
        offerAliasId: this.offerAliasId,
      },
    });
  }

  goToEarlyCheckInTab(): void {
    this.sharedService.navigateToOfferTab('ECI');
  }

  goToLateCheckoutTab(): void {
    this.sharedService.navigateToOfferTab('LCO');
  }

  public goToAddonTab(): void {
    this.sharedService.navigateToOfferTab('ANCILLARY');
  }

  public checkOut() {
    this.shoppingCartService.openCheckOutOfferTermsModal(this.offerAliasId, this.propertyId);
  }

  goToNextTab(): void {
    const offersInCart = this.shoppingCartService.getNumberOfOffersInShoppingCart(this.offerAliasId);
    const next = this.sharedService.getNextUpgradeAction(offersInCart);

    if (!next) return;
    if (next.action === 'checkout') {
      this.checkOut();
      return;
    }

    this.sharedService.navigateToOfferTab(next.tab);
  }

  addToCartAndContinue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    try {
      this.addOfferToCart();
    } finally {
      this.goToNextTab();
    }
  }

  addOfferToCart(): void {
    if (this.offer) {
      this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, true);
    }
  }

  removeOfferFromCart(): void {
    if (this.offer) {
      this.shoppingCartService.removeOfferFromCart(this.offer, this.offerAliasId);
    }
  }

  requestUpgrade(): void {
    if (this.offer) {
      this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, false);
      this.shoppingCartService.openCheckOutOfferTermsModal(this.offerAliasId, this.propertyId);
    }
  }

  checkIfOfferInCart(): boolean {
    return this.shoppingCartService.checkIfOfferInCart(
      this.offerAliasId,
      this.offer?.offer_set_id || '',
      this.offer?.offer_id || '',
    );
  }

  checkIfDisabled(): boolean {
    const offers = this.shoppingCartService.getOffersByAliasId(this.offerAliasId);
    
    // Offers with ofr_category 'ANCILLARY' are allowed to have multiple 
    // entries in the cart, so they are never disabled
    if (this.offerCategoryService.isOfferInCategory(this.offers?.[0], 'ANCILLARY')) {
      return false;
    }

    if (!offers || !this.offer) {
      return false;
    }

    const exactMatch = offers.find(entry =>
      entry.offerSetId === this.offer!.offer_set_id && 
      entry.offerId === this.offer!.offer_id
    );

    const offerSetMatch = offers.find(entry =>
      entry.offerSetId === this.offer!.offer_set_id
    );

    // Disabled if there's a different offer in the same set
    return !exactMatch && !!offerSetMatch;
  }

  toggleInfoTooltip(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.infoTooltipOpen = !this.infoTooltipOpen;
  }

  closeInfoTooltip(): void {
    this.infoTooltipOpen = false;
  }

  clipRoomText(text: string, maxLength: number): string {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  }

}
